import { searchMatches } from "@majourneys/shared";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { useDocumentDir, useDropdown, useNarrowViewport, menuKeys, useRoving } from "./keys";
import { useMenuStyles } from "./menu-styles";
import {
  CountTag,
  EmptyRow,
  MenuScroll,
  MenuSearchField,
  MenuSurface,
  MenuTrigger,
  OptionRow,
  type MenuTriggerRenderProps,
} from "./parts";
import { usePresence, type MenuGroup } from "./presence";

/**
 * The house dropdown: a menu on a desk, a sheet on a phone, with the search
 * field switched on by itself once the list passes about ten rows.
 *
 * One control, two bodies, because the two screens want different things from
 * the same question. On a wide screen the answer hangs off the corner of the
 * chip that asked it. On a phone it comes up from the bottom instead: full
 * width, rows a thumb can hit, a handle and a title so the choice has a name,
 * and a scrim over what is behind.
 *
 * The search field is not a separate control. A list of four does not need one
 * and a list of thirteen is a scroll without one, so it appears on its own.
 */

export interface MenuOption {
  readonly value: string;
  readonly label: string;
  /** A second line under the label. */
  readonly meta?: string;
  /** Shown on the end of the row: how many spots this choice leads to. */
  readonly count?: number;
  /** A thumbnail or a glyph in front of the label. */
  readonly lead?: ReactNode;
}

/** Every string a traveller can read. Passed in, so the package holds no copy. */
export interface SelectMenuLabels {
  /** The sheet's heading, and the panel's accessible name. */
  readonly title: string;
  readonly searchPlaceholder: string;
  readonly clearSearch: string;
  readonly closeList: string;
  readonly listLabel?: string;
  readonly emptyTitle: string;
  readonly emptyHint?: string;
  /** Given what was typed, so a miss is never a blank box. */
  readonly noMatchTitle: (query: string) => string;
  readonly noMatchHint?: string;
}

export interface SelectMenuProps {
  readonly name: string;
  readonly options: readonly MenuOption[];
  readonly value: string | null;
  readonly onChange: (next: string) => void;
  readonly labels: SelectMenuLabels;
  readonly group?: MenuGroup;
  /**
   * Goes on the box that holds the trigger and the panel. The box also carries
   * `data-open` and `data-chosen`, so a consumer's own stylesheet can dress its
   * trigger without the component knowing anything about that stylesheet.
   */
  readonly className?: string;
  /** Merged into that same box. Inline, because a Tailwind class written in a
   *  package is emitted only by accident: the app's build does not scan here. */
  readonly style?: CSSProperties;
  /** Shown on the trigger while nothing is chosen. */
  readonly placeholder?: string;
  readonly lead?: ReactNode;
  readonly side?: "start" | "end";
  readonly width?: number | string;
  readonly full?: boolean;
  readonly triggerSize?: "sm" | "md";
  readonly triggerLabel?: string;
  readonly triggerAriaLabel?: string;
  /** `auto` follows the screen; the other two are for a surface that only ever
   *  exists on one of them, and for tests. */
  readonly body?: "auto" | "menu" | "sheet";
  /** The width the sheet body takes over. Defaults to a phone. */
  readonly sheetMaxWidth?: number;
  /** How many rows before the search field appears. About ten. */
  readonly searchThreshold?: number;
  /** Draw your own trigger and keep the behaviour. */
  readonly renderTrigger?: (props: MenuTriggerRenderProps) => ReactNode;
  /** The lab's frozen examples open themselves on mount. */
  readonly defaultOpen?: boolean;
  readonly openNonce?: number;
}

export function SelectMenu({
  name,
  options,
  value,
  onChange,
  labels,
  group,
  className,
  style,
  placeholder,
  lead,
  side = "end",
  width = 240,
  full,
  triggerSize = "sm",
  triggerLabel,
  triggerAriaLabel,
  body = "auto",
  sheetMaxWidth = 639,
  searchThreshold = 10,
  renderTrigger,
  defaultOpen,
  openNonce,
}: SelectMenuProps) {
  useMenuStyles();
  const dir = useDocumentDir();
  const narrow = useNarrowViewport(sheetMaxWidth);
  const sheet = body === "sheet" || (body === "auto" && narrow);

  const surfaceRef = useRef<HTMLElement | null>(null);
  const dd = useDropdown(name, { group, surfaceRef, defaultOpen, openNonce });
  const { mounted, state, ref: presenceRef } = usePresenceFor(dd.open, sheet);

  const [query, setQuery] = useState("");
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const searchable = options.length > searchThreshold;

  const shown = useMemo(() => {
    if (!searchable) return options;
    const q = query.trim();
    if (!q) return options;
    // "meknes" finds Meknès: accents and case ignored (2026-11-04).
    return options.filter((o) => searchMatches(q, o.label));
  }, [options, query, searchable]);

  const selectedIndex = shown.findIndex((o) => o.value === value);
  const { active, setActive, listRef } = useRoving(
    dd.open,
    shown.length,
    Math.max(0, selectedIndex),
  );

  /* The field takes focus as the panel lands rather than waiting for a second
     press. Tied to the open flag rather than fired from the press: a timer left
     running by the press that *closes* the menu would pull focus back into a
     panel on its way out, and the trigger would never get it. */
  useEffect(() => {
    if (!dd.open || !searchable) return;
    const timer = window.setTimeout(() => fieldRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [dd.open, searchable]);

  // A menu that closes forgets what was typed into it.
  useEffect(() => {
    if (!dd.open) setQuery("");
  }, [dd.open]);

  const commit = (index: number) => {
    const option = shown[index];
    if (!option) return;
    onChange(option.value);
    setQuery("");
    dd.setOpen(false);
  };

  const chosen = options.find((o) => o.value === value) ?? null;
  const label = triggerLabel ?? chosen?.label ?? placeholder ?? labels.title;

  /* A panel holding a search field, or a sheet holding a handle and a title,
     is a dialog with a list inside it, not a list. Rows with role="option"
     under anything but a listbox are rows a screen reader cannot count. */
  const dialog = sheet || searchable;

  const rows =
    shown.length === 0 ? (
      query ? (
        <EmptyRow title={labels.noMatchTitle(query)} hint={labels.noMatchHint} />
      ) : (
        <EmptyRow title={labels.emptyTitle} hint={labels.emptyHint} />
      )
    ) : (
      <MenuScroll
        listRef={listRef}
        max={sheet ? 300 : 264}
        role={dialog ? "listbox" : undefined}
        ariaLabel={dialog ? (labels.listLabel ?? labels.title) : undefined}
      >
        {shown.map((option, index) => (
          <OptionRowFor
            key={option.value || "__all"}
            option={option}
            index={index}
            active={index === active}
            selected={option.value === value}
            size={sheet ? "lg" : "md"}
            onHover={() => setActive(index)}
            onClick={() => commit(index)}
          />
        ))}
      </MenuScroll>
    );

  const triggerProps: MenuTriggerRenderProps = {
    ref: dd.triggerRef,
    open: dd.open,
    chosen: !!chosen,
    label,
    buttonProps: {
      type: "button",
      onClick: dd.toggle,
      "aria-haspopup": dialog ? "dialog" : "listbox",
      "aria-expanded": dd.open,
    },
  };

  return (
    <div
      ref={dd.ref}
      className={className}
      data-open={dd.open ? "true" : undefined}
      data-chosen={chosen ? "true" : undefined}
      style={{ position: "relative", display: full ? "block" : "inline-block", ...style }}
      onKeyDown={menuKeys({
        open: dd.open,
        setOpen: dd.setOpen,
        count: shown.length,
        active,
        setActive,
        commit,
      })}
    >
      {renderTrigger ? (
        renderTrigger(triggerProps)
      ) : (
        <MenuTrigger
          ref={dd.triggerRef}
          open={dd.open}
          chosen={!!chosen}
          full={full}
          size={triggerSize}
          lead={lead}
          value={label}
          ariaLabel={triggerAriaLabel}
          ariaHasPopup={dialog ? "dialog" : "listbox"}
          onClick={dd.toggle}
        />
      )}

      {mounted ? (
        <MenuSurface
          presenceRef={presenceRef}
          surfaceRef={surfaceRef}
          state={state}
          instant={dd.instant}
          sheet={sheet}
          side={side}
          width={full ? "100%" : width}
          role={dialog ? "dialog" : "listbox"}
          ariaLabel={labels.title}
          title={labels.title}
          closeLabel={labels.closeList}
          onClose={dd.close}
          dir={dir}
        >
          {searchable ? (
            <MenuSearchField
              inputRef={fieldRef}
              value={query}
              onChange={(next) => {
                setQuery(next);
                setActive(0);
              }}
              placeholder={labels.searchPlaceholder}
              clearLabel={labels.clearSearch}
            />
          ) : null}
          {rows}
        </MenuSurface>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** The sheet's own entrance is slower than the menu's, so it outlives it. */
function usePresenceFor(open: boolean, sheet: boolean) {
  return usePresence(open, sheet ? 260 : undefined);
}

function OptionRowFor({
  option,
  index,
  active,
  selected,
  size,
  onHover,
  onClick,
}: {
  option: MenuOption;
  index: number;
  active: boolean;
  selected: boolean;
  size: "md" | "lg";
  onHover: () => void;
  onClick: () => void;
}) {
  return (
    <OptionRow
      index={index}
      active={active}
      selected={selected}
      size={size}
      lead={option.lead}
      title={option.label}
      meta={option.meta}
      trailing={option.count === undefined ? undefined : <CountTag>{option.count}</CountTag>}
      onHover={onHover}
      onClick={onClick}
    />
  );
}
